import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@/shared/components/feedback/ErrorState/ErrorState';
import { Skeleton } from '@/shared/components/feedback/Skeleton/Skeleton';
import { Badge } from '@/shared/components/ui/Badge/Badge';

import { type ViolationConsequenceProps } from './ViolationConsequence.types';
import { useViolationConsequenceLogic } from './useViolationConsequenceLogic';

/**
 * What confirming this rule will do, in words, read live from the rule.
 *
 * The dialog's whole job is to make a consequence legible before it is applied,
 * so this is the one thing on it that must never be approximate: the suspension
 * length is part of the sentence, and a rule that could not be read renders an
 * error rather than a reassuring blank.
 */
export function ViolationConsequence({ violationTypeId, label }: ViolationConsequenceProps) {
  const { t } = useTranslation('violations');
  const { type, copy, isLoading, error, reload } = useViolationConsequenceLogic(violationTypeId);

  return (
    <section className="flex flex-col gap-xs rounded-md bg-muted p-md">
      <h3 className="text-caption font-medium text-foreground">{label}</h3>

      {violationTypeId === null ? (
        <p className="text-body text-muted-foreground">{t('classify.chooseFirst')}</p>
      ) : isLoading ? (
        <Skeleton className="h-6 w-48" />
      ) : error !== null ? (
        // Never a silent fallback: a confirmation that cannot say what it will
        // do must not look like one that can.
        <ErrorState error={error} title={t('error.typeNotFound')} onRetry={reload} />
      ) : type === null || copy === null ? null : (
        <div className="flex flex-wrap items-center gap-sm">
          <Badge tone={copy.tone}>{t(copy.i18nKey, { count: copy.count })}</Badge>
          <span className="text-caption text-muted-foreground">
            {type.name} <span dir="ltr">({type.key})</span>
          </span>
          {type.reviewBeforeEnforce ? (
            <span className="flex items-center gap-2xs text-caption text-muted-foreground">
              <AlertTriangle aria-hidden className="h-3.5 w-3.5 shrink-0" />
              {t('types.humanFirst')}
            </span>
          ) : null}
        </div>
      )}
    </section>
  );
}
